import { EntityState } from './entity-state';
import type {
    EntitySearchArg,
    PageParam,
    PageResult,
    PagedMetaEnvelope,
    PagedQueryArg,
} from './paged-types';
import { SEARCH_RESULT_LIMIT } from './paged-types';
import { buildPagedQueryString, buildSearchWhere } from './query-params';

export type DatasetListItem = {
    id: string;
    name: string;
    description: string;
    copyright: string;
    lengthUnits: string;
    defaultCoreDiameter: number | null;
    sequence: number;
    state: string;
    workspaceId: string;
    subscriptionId: string;
    tenantId: string;
    containerId: string;
    createdAt: string;
    updatedAt: string;
};

type DatasetApiItem = {
    id?: string;
    name?: string | null;
    description?: string | null;
    copyright?: string | null;
    lengthUnits?: string | null;
    defaultCoreDiameter?: number | null;
    sequence?: number | null;
    state?: string | null;
    workspaceId?: string | null;
    subscriptionId?: string | null;
    tenantId?: string | null;
    containerId?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
};

type DatasetsApiResponse = {
    datasets?: DatasetApiItem[];
    meta?: PagedMetaEnvelope;
};

type DatasetApiItemWithId = DatasetApiItem & { id: string };

const hasId = (dataset: DatasetApiItem): dataset is DatasetApiItemWithId =>
    typeof dataset.id === 'string';

const transformDataset = (dataset: DatasetApiItemWithId): DatasetListItem => ({
    id: dataset.id,
    name: dataset.name ?? '',
    description: dataset.description ?? '',
    copyright: dataset.copyright ?? '',
    lengthUnits: dataset.lengthUnits ?? '',
    defaultCoreDiameter: dataset.defaultCoreDiameter ?? null,
    sequence: dataset.sequence ?? 0,
    state: dataset.state ?? EntityState.INACTIVE,
    workspaceId: dataset.workspaceId ?? '',
    subscriptionId: dataset.subscriptionId ?? '',
    tenantId: dataset.tenantId ?? '',
    containerId: dataset.containerId ?? '',
    createdAt: dataset.createdAt ?? '',
    updatedAt: dataset.updatedAt ?? '',
});

export type DatasetsPageResult = PageResult<DatasetListItem>;
export type DatasetsQueryArg = PagedQueryArg;

const DATASET_SEARCH_COLUMNS = ['name', 'description', 'copyright'] as const;
const DATASET_UUID_COLUMNS = ['id'] as const;

export const searchDatasetsQuery = {
    query: ({
        term,
        limit = SEARCH_RESULT_LIMIT + 1,
        tenantId,
        subscriptionId,
        where,
    }: EntitySearchArg) => ({
        url: `/mp/1/datasets?${buildPagedQueryString({
            offset: 0,
            limit,
            order: [['name', 'ASC']],
            where: {
                ...buildSearchWhere(DATASET_SEARCH_COLUMNS, term, DATASET_UUID_COLUMNS),
                ...(subscriptionId ? { subscriptionId } : {}),
                ...where,
            },
            extraParams: tenantId ? { tenantId } : undefined,
        })}`,
        method: 'get',
    }),
    transformResponse: (response: DatasetsApiResponse): DatasetListItem[] =>
        (response?.datasets ?? []).filter(hasId).map(transformDataset),
};

export const datasetsInfiniteQuery = {
    query: ({ queryArg, pageParam }: { queryArg: DatasetsQueryArg; pageParam: PageParam }) => ({
        url: `/mp/1/datasets?${buildPagedQueryString({
            offset: pageParam.offset,
            limit: queryArg.limit,
            includeCount: true,
            order: queryArg.order,
            where: queryArg.where,
            extraParams: queryArg.extraParams,
        })}`,
        method: 'get',
    }),
    transformResponse: (response: DatasetsApiResponse): DatasetsPageResult => {
        const rows = (response?.datasets ?? []).filter(hasId).map(transformDataset);
        return {
            rows,
            total: response?.meta?.total ?? rows.length,
            offset: response?.meta?.offset ?? 0,
            limit: response?.meta?.limit ?? rows.length,
        };
    },
    keepUnusedDataFor: 300,
    infiniteQueryOptions: {
        initialPageParam: { offset: 0 },
        getNextPageParam: (
            lastPage: DatasetsPageResult,
            _allPages: DatasetsPageResult[],
            lastPageParam: PageParam,
        ): PageParam | undefined => {
            if (lastPage.rows.length === 0) return undefined;
            const loaded = lastPageParam.offset + lastPage.rows.length;
            return loaded < lastPage.total ? { offset: loaded } : undefined;
        },
        getPreviousPageParam: (): PageParam | undefined => undefined,
    },
};
